import React, { useMemo, useState } from 'react'

import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    IconButton,
    CircularProgress,
    TextField,
    InputAdornment,
} from '@material-ui/core'
import {
    Add as AddIcon,
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
} from '@material-ui/icons'
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles'

import { useGuestCards } from '../../../../hooks/Outreach/useGuestCards'
import { useGuestCardChannelDefaults } from '../../../../hooks/Outreach/useGuestCardChannelDefaults'
import { CreateGuestCardModal } from './CreateGuestCardModal'
import { GuestCard } from '../../../../models/Outreach/types'
import { GUEST_CARD_FIELD_QUESTIONS } from '../../../../models/Outreach/services'
import { EditGuestCardModal } from './EditGuestCardModal'

const useStyles = makeStyles((theme: Theme) => ({
    pageContainer: {
        padding: theme.spacing(4),
        maxWidth: '1400px',
        margin: '0 auto',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(4),
        paddingBottom: theme.spacing(3),
        borderBottom: '1px solid rgba(226, 232, 240, 0.5)',
    },
    headerContent: {
        flex: 1,
    },
    headerTitle: {
        fontSize: '2.25rem',
        fontWeight: 700,
        color: '#1f2937',
        marginBottom: theme.spacing(0.5),
        letterSpacing: '-0.02em',
    },
    headerSubtitle: {
        fontSize: '1rem',
        color: '#6b7280',
        fontWeight: 400,
    },
    createButton: {
        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
        color: 'white',
        textTransform: 'none',
        fontWeight: 600,
        padding: theme.spacing(1.5, 3),
        borderRadius: '10px',
        boxShadow:
            '0 4px 6px -1px rgba(59, 130, 246, 0.3), 0 2px 4px -1px rgba(59, 130, 246, 0.2)',
        fontSize: '0.9375rem',
        '&:hover': {
            background: 'linear-gradient(135deg, #2563eb, #1e40af)',
            boxShadow:
                '0 10px 15px -3px rgba(59, 130, 246, 0.4), 0 4px 6px -2px rgba(59, 130, 246, 0.3)',
            transform: 'translateY(-1px)',
        },
        transition: 'all 0.2s ease',
    },
    searchField: {
        marginBottom: theme.spacing(4),
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: '#f9fafb',
            '&:hover': {
                backgroundColor: '#ffffff',
            },
            '&.Mui-focused': {
                backgroundColor: '#ffffff',
            },
        },
    },
    guestCardCard: {
        border: '1px solid #e5e7eb',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        overflow: 'hidden',
        position: 'relative',
        '&:hover': {
            boxShadow:
                '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            transform: 'translateY(-4px)',
            borderColor: '#3b82f6',
        },
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: theme.spacing(2.5),
        paddingBottom: theme.spacing(2),
        borderBottom: '1px solid #f3f4f6',
    },
    cardTitleContainer: {
        flex: 1,
        minWidth: 0,
    },
    cardTitle: {
        fontSize: '1.125rem',
        fontWeight: 600,
        color: '#111827',
        marginBottom: theme.spacing(0.75),
        lineHeight: 1.4,
    },
    defaultBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 10px',
        borderRadius: '6px',
        backgroundColor: '#dbeafe',
        color: '#1e40af',
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    },
    cardActions: {
        display: 'flex',
        gap: theme.spacing(0.5),
        marginLeft: theme.spacing(1),
    },
    actionButton: {
        padding: theme.spacing(1),
        color: '#6b7280',
        borderRadius: '8px',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: '#f3f4f6',
            color: '#3b82f6',
            transform: 'scale(1.1)',
        },
    },
    fieldsList: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5),
    },
    sectionHeader: {
        fontSize: '0.75rem',
        fontWeight: 700,
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    sectionCount: {
        backgroundColor: '#e5e7eb',
        color: '#4b5563',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.6875rem',
        fontWeight: 600,
    },
    fieldList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    fieldItem: {
        display: 'flex',
        alignItems: 'flex-start',
        padding: theme.spacing(1, 1.5),
        borderRadius: '8px',
        backgroundColor: '#f9fafb',
        fontSize: '0.875rem',
        color: '#374151',
        lineHeight: 1.5,
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: '#f3f4f6',
        },
    },
    fieldCheck: {
        color: '#10b981',
        marginRight: theme.spacing(1),
        fontSize: '1rem',
        marginTop: '2px',
        flexShrink: 0,
    },
    fieldText: {
        flex: 1,
        wordBreak: 'break-word',
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(10, 4),
    },
    emptyStateIcon: {
        fontSize: '5rem',
        color: '#d1d5db',
        marginBottom: theme.spacing(3),
        opacity: 0.5,
    },
    emptyStateTitle: {
        fontSize: '1.5rem',
        fontWeight: 600,
        color: '#111827',
        marginBottom: theme.spacing(1),
    },
    emptyStateText: {
        fontSize: '1rem',
        color: '#6b7280',
        maxWidth: '400px',
        margin: '0 auto',
        marginBottom: theme.spacing(3),
    },
}))

export const GuestCardsPage: React.FC = () => {
    const classes = useStyles()
    const theme = useTheme()
    const { guestCards, isLoadingGuestCards, errorGuestCards } = useGuestCards()
    const { defaults: guestCardDefaults } = useGuestCardChannelDefaults()
    const defaultCampaignGuestCardId = guestCardDefaults.campaign
    const [searchQuery, setSearchQuery] = useState('')
    const [createModalOpen, setCreateModalOpen] = useState(false)
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [
        selectedGuestCard,
        setSelectedGuestCard,
    ] = useState<GuestCard | null>(null)

    const handleEdit = (guestCard: GuestCard) => {
        setSelectedGuestCard(guestCard)
        setEditModalOpen(true)
    }

    const handleCreateSuccess = () => {
        setCreateModalOpen(false)
    }

    const handleEditSuccess = () => {
        setEditModalOpen(false)
        setSelectedGuestCard(null)
    }

    const filteredGuestCards = useMemo(() => {
        const filtered =
            guestCards?.filter((card) =>
                card.card_name
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()),
            ) || []
        if (defaultCampaignGuestCardId == null) return filtered
        return [...filtered].sort((a, b) => {
            if (a.id === defaultCampaignGuestCardId) return -1
            if (b.id === defaultCampaignGuestCardId) return 1
            return 0
        })
    }, [guestCards, searchQuery, defaultCampaignGuestCardId])

    const getEnabledFields = (
        card: GuestCard,
    ): Array<keyof typeof GUEST_CARD_FIELD_QUESTIONS> => {
        const fields: Array<keyof typeof GUEST_CARD_FIELD_QUESTIONS> = []
        if (card.name) fields.push('name')
        if (card.email) fields.push('email')
        if (card.phone) fields.push('phone')
        if (card.preferred_unit_config) fields.push('preferred_unit_config')
        if (card.preferred_premiums) fields.push('preferred_premiums')
        if (card.preferred_rent_cost) fields.push('preferred_rent_cost')
        if (card.preferred_move_in_date) fields.push('preferred_move_in_date')
        if (card.preferred_move_out_date) fields.push('preferred_move_out_date')
        if (card.preferred_roommates) fields.push('preferred_roommates')
        if (card.school_attendance) fields.push('school_attendance')
        if (card.additional_requests) fields.push('additional_requests')
        return fields
    }

    if (isLoadingGuestCards) {
        return (
            <Box
                className={classes.pageContainer}
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                }}
            >
                <CircularProgress />
            </Box>
        )
    }

    if (errorGuestCards) {
        return (
            <Box className={classes.pageContainer}>
                <Typography variant="h6" color="error">
                    Failed to load guest cards
                </Typography>
            </Box>
        )
    }

    return (
        <Box className={classes.pageContainer}>
            {/* Header */}
            <Box className={classes.header}>
                <Box className={classes.headerContent}>
                    <Typography className={classes.headerTitle}>
                        Guest Cards
                    </Typography>
                    <Typography className={classes.headerSubtitle}>
                        Manage guest card templates for your campaigns
                    </Typography>
                </Box>
                <Button
                    className={classes.createButton}
                    startIcon={<AddIcon />}
                    onClick={() => setCreateModalOpen(true)}
                >
                    Create Guest Card
                </Button>
            </Box>

            {/* Search */}
            <TextField
                className={classes.searchField}
                placeholder="Search guest cards..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                variant="outlined"
                fullWidth
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon style={{ color: '#6b7280' }} />
                        </InputAdornment>
                    ),
                }}
            />

            {/* Guest Cards Grid */}
            {filteredGuestCards.length > 0 ? (
                <Grid container spacing={3}>
                    {filteredGuestCards.map((card) => {
                        const enabledFields = getEnabledFields(card)
                        return (
                            <Grid item xs={12} sm={6} md={4} key={card.id}>
                                <Card className={classes.guestCardCard}>
                                    <CardContent>
                                        <Box className={classes.cardHeader}>
                                            <Box
                                                className={
                                                    classes.cardTitleContainer
                                                }
                                            >
                                                <Typography
                                                    className={
                                                        classes.cardTitle
                                                    }
                                                >
                                                    {card.card_name}
                                                </Typography>
                                                {card.id ===
                                                    defaultCampaignGuestCardId && (
                                                    <Box
                                                        className={
                                                            classes.defaultBadge
                                                        }
                                                    >
                                                        Campaign default
                                                    </Box>
                                                )}
                                                {card.default &&
                                                    card.id !==
                                                        defaultCampaignGuestCardId && (
                                                        <Box
                                                            className={
                                                                classes.defaultBadge
                                                            }
                                                        >
                                                            Default
                                                        </Box>
                                                    )}
                                            </Box>
                                            <Box
                                                className={classes.cardActions}
                                            >
                                                <IconButton
                                                    className={
                                                        classes.actionButton
                                                    }
                                                    size="small"
                                                    onClick={() =>
                                                        handleEdit(card)
                                                    }
                                                    title="Edit guest card"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Box>

                                        <Box className={classes.fieldsList}>
                                            {/* Default Questions Section */}
                                            <Box>
                                                <Box
                                                    className={
                                                        classes.sectionHeader
                                                    }
                                                >
                                                    Default Questions
                                                    {enabledFields.length >
                                                        0 && (
                                                        <span
                                                            className={
                                                                classes.sectionCount
                                                            }
                                                        >
                                                            {
                                                                enabledFields.length
                                                            }
                                                        </span>
                                                    )}
                                                </Box>
                                                {enabledFields.length > 0 ? (
                                                    <Box
                                                        className={
                                                            classes.fieldList
                                                        }
                                                    >
                                                        {enabledFields.map(
                                                            (field) => (
                                                                <Box
                                                                    key={field}
                                                                    className={
                                                                        classes.fieldItem
                                                                    }
                                                                >
                                                                    <span
                                                                        className={
                                                                            classes.fieldCheck
                                                                        }
                                                                    >
                                                                        ✓
                                                                    </span>
                                                                    <span
                                                                        className={
                                                                            classes.fieldText
                                                                        }
                                                                    >
                                                                        {
                                                                            GUEST_CARD_FIELD_QUESTIONS[
                                                                                field
                                                                            ]
                                                                        }
                                                                    </span>
                                                                </Box>
                                                            ),
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            color: '#9ca3af',
                                                            fontStyle: 'italic',
                                                            padding: theme.spacing(
                                                                1,
                                                                1.5,
                                                            ),
                                                        }}
                                                    >
                                                        No default questions
                                                        selected
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Custom Questions Section */}
                                            {card.card_questions &&
                                                card.card_questions.length >
                                                    0 && (
                                                    <Box>
                                                        <Box
                                                            className={
                                                                classes.sectionHeader
                                                            }
                                                        >
                                                            Custom Questions
                                                            <span
                                                                className={
                                                                    classes.sectionCount
                                                                }
                                                            >
                                                                {
                                                                    card
                                                                        .card_questions
                                                                        .length
                                                                }
                                                            </span>
                                                        </Box>
                                                        <Box
                                                            className={
                                                                classes.fieldList
                                                            }
                                                        >
                                                            {card.card_questions.map(
                                                                (
                                                                    cardQuestion,
                                                                    index,
                                                                ) => (
                                                                    <Box
                                                                        key={
                                                                            cardQuestion.id ||
                                                                            index
                                                                        }
                                                                        className={
                                                                            classes.fieldItem
                                                                        }
                                                                    >
                                                                        <span
                                                                            className={
                                                                                classes.fieldCheck
                                                                            }
                                                                        >
                                                                            ✓
                                                                        </span>
                                                                        <span
                                                                            className={
                                                                                classes.fieldText
                                                                            }
                                                                        >
                                                                            {
                                                                                cardQuestion
                                                                                    .custom_question
                                                                                    .question
                                                                            }
                                                                        </span>
                                                                    </Box>
                                                                ),
                                                            )}
                                                        </Box>
                                                    </Box>
                                                )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )
                    })}
                </Grid>
            ) : (
                <Card
                    className={classes.guestCardCard}
                    style={{
                        border: '2px dashed #e5e7eb',
                        backgroundColor: '#fafafa',
                    }}
                >
                    <CardContent className={classes.emptyState}>
                        <Typography className={classes.emptyStateIcon}>
                            📋
                        </Typography>
                        <Typography className={classes.emptyStateTitle}>
                            {searchQuery
                                ? 'No guest cards found'
                                : 'No guest cards yet'}
                        </Typography>
                        <Typography className={classes.emptyStateText}>
                            {searchQuery
                                ? "Try adjusting your search query to find what you're looking for"
                                : 'Create your first guest card template to start collecting information from prospects'}
                        </Typography>
                        {!searchQuery && (
                            <Button
                                className={classes.createButton}
                                startIcon={<AddIcon />}
                                onClick={() => setCreateModalOpen(true)}
                            >
                                Create Guest Card
                            </Button>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Modals */}
            <CreateGuestCardModal
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onGuestCardCreated={handleCreateSuccess}
            />

            {selectedGuestCard && (
                <EditGuestCardModal
                    open={editModalOpen}
                    onClose={() => {
                        setEditModalOpen(false)
                        setSelectedGuestCard(null)
                    }}
                    guestCard={selectedGuestCard}
                    onGuestCardUpdated={handleEditSuccess}
                />
            )}
        </Box>
    )
}
